import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import useSessions from "@/features/sessions/hooks/use-sessions";
import { useUpdateAvailability } from "@/features/sessions/hooks/use-update-availability";
import { SessionCard } from "@/features/sessions/ui/session-card";
import { SearchBar } from "@/components/common/search-bar";
import { SessionFilters } from "@/features/sessions/ui/session-filters";
import { SessionDetailsDrawer } from "@/features/sessions/ui/session-details-drawer";
import { UserDetailsDrawer } from "@/features/users/ui/user-details-drawer";
import { toast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { XIcon } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { useState } from "react";

export default function Sessions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "0", 10);
  const usage_id = searchParams.get("usage_id") ?? undefined;
  const { mutate: updateAvailability } = useUpdateAvailability();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);

  const product_id = searchParams.get("product_id") || undefined;
  const professional_id = searchParams.get("professional_id") || undefined;
  const search = searchParams.get("search") || "";

  // Build filter string from URL params
  const filterParts: string[] = [];
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const status = searchParams.get("status");
  const tag = searchParams.get("tag");

  if (startDate) filterParts.push(`start_date::${startDate}`);
  if (endDate) filterParts.push(`end_date::${endDate}`);
  if (status && status !== "all") filterParts.push(`status::${status}`);
  if (tag && tag !== "all") filterParts.push(`tag::${tag}`);

  const filter = filterParts.join(",");

  // hasFilters + handleClearFilters were moved out of SessionFilters (its old
  // unused handleReset) so the Clear filters button can render beside the
  // search bar, which lives on this page rather than inside the filter row.
  const hasFilters = Boolean(
    startDate ||
      endDate ||
      (status && status !== "all") ||
      (tag && tag !== "all") ||
      (professional_id && professional_id !== "ALL") ||
      (product_id && product_id !== "ALL")
  );

  // Only rewrites the URL — SessionFilters watches the params in a useEffect
  // and resets its own form state, so the dropdowns/date picker snap back to
  // defaults without this page reaching into the form.
  const handleClearFilters = () => {
    const newParams = new URLSearchParams();
    newParams.set("page", "0");
    // Search text and usage_id are not filters — keep them across a clear
    if (search) newParams.set("search", search);
    if (usage_id) newParams.set("usage_id", usage_id);
    setSearchParams(newParams);
  };

  const { data, error, isFetching, isLoading } = useSessions(
    page,
    search,
    filter,
    usage_id,
    professional_id,
    product_id
  );

  const sessions = data?.sessions || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());
      if (usage_id) newParams.set("usage_id", usage_id);
      setSearchParams(newParams);
    }
  };

  return (
    <div className="w-full">
      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <SessionFilters usage_id={usage_id} />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Sessions"
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />
              }}
              fieldSelector={{
                enabled: false,
                fields: []
              }}
              className="w-full"
            />
          </div>
          {hasFilters && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClearFilters}
              className="bg-white text-gray-900 border-gray-300 hover:bg-gray-50"
            >
              <XIcon className="mr-1 h-4 w-4" />
              Clear filters
            </Button>
          )}
        </div>
      </div>

      <ContentLayout
        header={null}
      data={sessions}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load sessions"
      emptyState={{
        message: "No sessions found"
      }}
    >
      {sessions.map((session: any) => (
        <SessionCard 
          key={session.id} 
          session={session}
          onCardClick={() => {
            setSelectedSessionId(session.id);
            setIsDrawerOpen(true);
          }}
          onUserClick={(userId) => {
            setSelectedUserId(userId);
            setIsUserDrawerOpen(true);
          }}
        />
      ))}
      <SessionDetailsDrawer
        sessionId={selectedSessionId}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isUserDrawerOpen}
        onOpenChange={setIsUserDrawerOpen}
      />
      {sessions.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
      <HasAccess permission="write">
        <div className="space-y-6 mt-6">
          <Button
            className="bg-sidebar"
            onClick={() => {
              toast({
                title: "Updating availabilities...",
                description: "Please wait...this may take a few seconds.",
              });
              updateAvailability();
            }}
          >
            Update availabilities
          </Button>
          <small>
            {" "}
            This updates the availabilities of next 60 days. View this data in
            Metabase.
          </small>
        </div>
      </HasAccess>
    </ContentLayout>
    </div>
  );
}
